import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { Badge, badgeClasses, IconButton, Theme } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useMatch, useNavigate } from 'react-router';
import { RoutePaths } from '../routes/AppRoutes';
import { getStateLayerColor, StateLayer } from '../theme/color.utils';
import { canAccessWishlistAtom, wishlistItemsAtom, wishlistSupplierAtom } from './state';

export default function WishlistButton(): React.JSX.Element | null {
  const navigate = useNavigate();
  const canAccessWishlist = useAtomValue(canAccessWishlistAtom);
  const matchWishlistPage = useMatch(RoutePaths.WISHLIST);
  const wishlistItems = useAtomValue(wishlistItemsAtom);
  const wishlistSuppliers = useAtomValue(wishlistSupplierAtom);
  const wishlistItemCount = wishlistItems.length + wishlistSuppliers.length;

  if (!canAccessWishlist) {
    return null;
  }

  return (
    <IconButton
      onClick={() => navigate(RoutePaths.WISHLIST)}
      sx={(theme: Theme) => {
        const favoriteColor = theme.palette.favorite.main;
        const containerColor = theme.palette.surfaceContainerLowest.main;
        const inactiveIconColor = theme.palette.onSurfaceVariant.main;
        const activeBackground = getStateLayerColor(StateLayer.Layer12, containerColor, favoriteColor);

        if (matchWishlistPage) {
          return {
            width: 40,
            height: 40,
            color: favoriteColor,
            backgroundColor: activeBackground,
            '&:hover': {
              color: favoriteColor,
              backgroundColor: getStateLayerColor(StateLayer.Layer16, containerColor, favoriteColor),
            },
            '&:focus': {
              color: favoriteColor,
              backgroundColor: activeBackground,
            },
            '&:focus:not(:focus-visible)': {
              backgroundColor: activeBackground,
            },
            '&:active': {
              color: favoriteColor,
              backgroundColor: getStateLayerColor(StateLayer.Layer16, containerColor, favoriteColor),
            },
          };
        } else {
          return {
            width: 40,
            height: 40,
            color: inactiveIconColor,
            backgroundColor: containerColor,
            '&:hover': {
              color: inactiveIconColor,
              backgroundColor: getStateLayerColor(StateLayer.Layer8, containerColor, inactiveIconColor),
            },
            '&:focus': {
              color: inactiveIconColor,
              backgroundColor: containerColor,
            },
            '&:focus:not(:focus-visible)': {
              backgroundColor: containerColor,
            },
            '&:active': {
              color: inactiveIconColor,
              backgroundColor: getStateLayerColor(StateLayer.Layer12, containerColor, inactiveIconColor),
            },
          };
        }
      }}
    >
      <Badge
        badgeContent={wishlistItemCount}
        color="error"
        invisible={!!matchWishlistPage || !wishlistItemCount}
        sx={{
          [`& .${badgeClasses.badge}`]: {
            paddingX: 0.5,
            fontFamily: 'Ambit',
            fontSize: '11px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '16px',
            letterSpacing: '0.5px',
          },
        }}
      >
        {matchWishlistPage ? <FavoriteIcon sx={{ fontSize: 24 }} /> : <FavoriteBorderIcon sx={{ fontSize: 24 }} />}
      </Badge>
    </IconButton>

  );
}
